import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField, Button, Box, Typography, IconButton, InputAdornment, Tabs, Tab, Card, CardMedia, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, FormGroup,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { useNavigate } from 'react-router-dom';
import api from 'core/interceptors/api';
import { disableBackNavigation } from 'core/utils/storage';
import { ServerBaseUrlForStaticImages } from 'constants';
import CircularProgress from '@mui/material/CircularProgress';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import Swal from 'sweetalert2';
import KeywordsInput from '../Product_Details/KeywordsInput';
import { styled } from '@mui/system';
import Autocomplete from '@mui/material/Autocomplete'; // Still imported but not used for Service Type input
import Chip from '@mui/material/Chip';
import ListSubheader from '@mui/material/ListSubheader';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

// Import missing components and assets
import Lottie from 'lottie-react';
import profileIncompleteAnimation from 'assets/images/animation.json';
import MDTypography from 'components/MDTypography';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';


import {
    validateBankAccountNumberOptional,
    validateDetails,
    validateEmailOptional,
    validateExperienceOptional,
    validateHomeAddress,
    validateIFSCCodeOptional,
    validateMobileNumberOptional,
    validateNameOnly,
    validateOperatingLocation,
    validateOwnerNameOptional,
    validatePanCardNumberOptional,
    validateRequired,
    validateServiceType,
    validateSpecializations,
    validateTextaloneOptional,
} from '../validations';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import VendorProfileWithCompletion from '../user_profile/vendor_profile_completion';
import LogoutButton from 'layouts/logout';


const UserProfile = () => {
    // 1. State and Ref Declarations
    const email = localStorage.getItem('email');
    const navigate = useNavigate();

    const [location, setLocation] = useState({ latitude: '', longitude: '', place: '' });
    const [isFetchingLocation, setIsFetchingLocation] = useState(false);
    const [availableDays, setAvailableDays] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // Master list of all service types with their specializations
    // This will be populated from the API and can also include user-added custom types.
    const [allServiceTypesData, setAllServiceTypesData] = useState([]);

    // Service types currently selected by the user (array of service type names)
    const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);

    // showCustomServiceTypeInput and customServiceType are no longer directly rendered
    // but kept in case there's an underlying logic still relying on their state.
    // For a truly static display, these could be removed if not used elsewhere.
    const [showCustomServiceTypeInput, setShowCustomServiceTypeInput] = useState(false);
    const [customServiceType, setCustomServiceType] = useState('');

    const [formErrors, setFormErrors] = useState({});
    const [completion, setCompletion] = useState(0);

    const [show100PercentCompleteBanner, setShow100PercentCompleteBanner] = useState(false);


    // Selected specializations (array of specialization names)
    const [selectedSpecializations, setSelectedSpecializations] = useState([]);

    // For adding new specializations to a specific service type
    const [newSpecializationInput, setNewSpecializationInput] = useState({});


    // Profile Data state
    const [profileData, setProfileData] = useState({
        ownerName: '',
        email: email || '',
        serviceType: [], // Initialize as array
        subService: '', // This will hold the raw API response for subservices, which we'll parse.
        details: '',
        workLocation: '',
        homeAddress: '',
        mobileNumber: '',
        experiance: '',
        accountNo: '',
        ifscCode: '',
        passbookName: '',
        availableDays: [], // Initialize as array
        panCardNumber: '',
        currentLocation: '',
        keywords: '',
        anotherMobileNumber: '',
        branchName: '',
        latitude: '',
        longitude: '',
        bankName: '',
        profilePhoto: null,
        idProof: null, // Multiple ID proofs
        workPhoto: null, // Multiple work photos
        workVideo: null,
        experianceCertificate: null, // Multiple experience certificates
        workPlaceGpsPhoto: null,
    });

    // 2. Memoized Callback Functions (using useCallback)

    const getImageUrl = useCallback((imagePath) => {
        if (!imagePath) return 'https://via.placeholder.com/150?text=No+Image';
        // Check if the path already contains the media folder, preventing double prefixes
        if (imagePath.includes('vendor_register/media') || imagePath.includes('services_register/media')) {
            return `${ServerBaseUrlForStaticImages}/${imagePath}`;
        }
        // Default to services_register/media if no specific folder mentioned
        return `${ServerBaseUrlForStaticImages}/services_register/media/${imagePath}`;
    }, []);


    const fetchCompletion = useCallback(async () => {
        try {
            const res = await api.get(`/services_register/profile-completion/${email}`);
            const percent = parseFloat(res.data.profile_completion.replace('%', ''));
            setCompletion(percent);

            const hasBeen100PercentNotified = localStorage.getItem('hasBeen100PercentNotified_UserProfile') === 'true';

            if (percent === 100) {
                setShow100PercentCompleteBanner(true);
                localStorage.setItem('hasBeen100PercentNotified_UserProfile', 'true');
                localStorage.setItem('completionReminderDismissed', 'false');
            } else {
                setShow100PercentCompleteBanner(false);
                if (hasBeen100PercentNotified) {
                    localStorage.setItem('hasBeen100PercentNotified_UserProfile', 'false');
                }
            }

        } catch (error) {
            console.error('Failed to fetch profile completion:', error);
        }
    }, [email]);


    const fetchAllServiceTypesWithSpecializations = useCallback(async () => {
        try {
            const response = await api.get(
                '/service_management/service_specializations/get_all_active_service_types_with_specializations',
            );

            const result = response.data?.data || [];

            const types = result.map((item) => {
                const rawSpecs = item.specializations || [];

                const cleanedSpecs = Array.isArray(rawSpecs)
                    ? rawSpecs.flatMap((spec) =>
                        spec
                            .split(',')
                            .map((s) => s.trim())
                            .filter(Boolean),
                    )
                    : typeof rawSpecs === 'string'
                        ? rawSpecs
                            .split(',')
                            .map((s) => s.trim())
                            .filter(Boolean)
                        : [];

                return {
                    id: item.id,
                    type_name: item.service_type,
                    specializations: [...new Set(cleanedSpecs)], // Unique specializations
                };
            });

            setAllServiceTypesData(types);
        } catch (error) {
            console.error('Error fetching service types and specializations:', error);
            setSnackbar({ open: true, message: 'Error fetching service categories!', severity: 'error' });
        }
    }, []);


    const fetchVendorProfile = useCallback(async (userEmail) => {
        try {
            const response = await api.get(`/services_register/approved_services?email=${userEmail}`);
            const services = response.data.service;

            if (services) {
                const rawDays = services?.availabiledays ?? '';
                let daysArray = [];

                if (Array.isArray(rawDays)) {
                    daysArray = rawDays.map(
                        (day) => day.charAt(0).toUpperCase() + day.slice(1).toLowerCase(),
                    );
                } else if (typeof rawDays === 'string' && rawDays.trim()) {
                    daysArray = rawDays
                        .split(',')
                        .map((day) => day.trim())
                        .map((day) => day.charAt(0).toUpperCase() + day.slice(1).toLowerCase());
                }

                let serviceTypeArray = [];
                if (typeof services.services_type === 'string' && services.services_type.trim()) {
                    serviceTypeArray = services.services_type.split(',').map((s) => s.trim());
                } else if (Array.isArray(services.services_type)) {
                    serviceTypeArray = services.services_type.map((s) => s.trim());
                }

                let subServiceCategoryArray = [];
                try {
                    const parsedSubService = typeof services.subservices === 'string' && services.subservices.trim() ? JSON.parse(services.subservices) : services.subservices;
                    if (parsedSubService && typeof parsedSubService === 'object') {
                        Object.values(parsedSubService).forEach((arr) => {
                            if (Array.isArray(arr)) {
                                arr.forEach((item) => {
                                    if (typeof item === 'string' && item.trim()) {
                                        subServiceCategoryArray.push(...item.split(',').map((s) => s.trim()).filter(Boolean));
                                    }
                                });
                            }
                        });
                        subServiceCategoryArray = [...new Set(subServiceCategoryArray)];
                    } else if (typeof services.subservices === 'string' && services.subservices.trim()) {
                        subServiceCategoryArray = services.subservices.split(',').map((s) => s.trim()).filter(Boolean);
                    }
                } catch (e) {
                    console.warn('Failed to parse subservices as JSON, attempting as comma-separated string.', e);
                    if (typeof services.subservices === 'string' && services.subservices.trim()) {
                        subServiceCategoryArray = services.subservices.split(',').map((s) => s.trim()).filter(Boolean);
                    }
                }

                setProfileData((prevData) => ({
                    ...prevData,
                    ownerName: services.owner_name || '',
                    email: services.email || '',
                    serviceType: serviceTypeArray, // Store as array for selectedServiceTypes
                    subService: services.subservices || '', // Keep original string/value for parsing in next effect
                    details: services.details || '',
                    workLocation: services.worklocation || '',
                    homeAddress: services.homeaddress || '',
                    experiance: services.experiance || '',
                    mobileNumber: services.mobile_number || '',
                    accountNo: services.account_no || '',
                    ifscCode: services.ifsc_code || '',
                    passbookName: services.passbook_name || '',
                    availableDays: daysArray, // Directly set, since it's already parsed
                    panCardNumber: services.pan_card_number || '',
                    currentLocation: services.currentLocation || '',
                    keywords: services.keywords || '',
                    anotherMobileNumber: services.anotherMobileNumber || '',
                    branchName: services.branchName || '',
                    latitude: services.latitude || '',
                    longitude: services.longitude || '',
                    bankName: services.bankName || '',
                    profilePhoto: services.profile_photo || null,
                    idProof: services.idproof_photo || null,
                    workPhoto: services.work_photo || null,
                    workVideo: services.work_video || null,
                    experianceCertificate: services.experience_certificates || null,
                    workPlaceGpsPhoto: services.work_place_gps || null,
                }));

                setAvailableDays(daysArray); // Update main availableDays state
                setSelectedServiceTypes(serviceTypeArray); // Update selected service types for Autocomplete
                setSelectedSpecializations(subServiceCategoryArray); // Update selected specializations
            }
        } catch (error) {
            console.error('There was an error fetching the profile data!', error);
            setSnackbar({ open: true, message: 'Error fetching profile data!', severity: 'error' });
        }
    }, [email, setSnackbar]);


    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setProfileData((prevData) => ({ ...prevData, [name]: value }));
    }, []);


    const handleCheckboxChange = useCallback((day) => {
        setAvailableDays((prevDays) =>
            prevDays.includes(day)
                ? prevDays.filter((d) => d !== day) // Remove if already selected
                : [...prevDays, day], // Add if not selected
        );
    }, []);


    const validateProfileForm = useCallback(() => {
        const errors = {};

        errors.ownerName = validateOwnerNameOptional(profileData.ownerName);
        errors.email = validateEmailOptional(profileData.email);
        errors.mobileNumber = validateMobileNumberOptional(profileData.mobileNumber);
        errors.anotherMobileNumber = validateMobileNumberOptional(profileData.anotherMobileNumber);
        // Removed validation for serviceType and subService here as they are now display-only
        errors.details = validateDetails(profileData.details);
        errors.workLocation = validateOperatingLocation(profileData.workLocation);
        errors.homeAddress = validateHomeAddress(profileData.homeAddress);
        errors.experiance = validateExperienceOptional(profileData.experiance);
        errors.accountNo = validateBankAccountNumberOptional(profileData.accountNo);
        errors.ifscCode = validateIFSCCodeOptional(profileData.ifscCode);
        errors.passbookName = validateNameOnly(profileData.passbookName);
        errors.branchName = validateTextaloneOptional(profileData.branchName);
        errors.bankName = validateTextaloneOptional(profileData.bankName);
        errors.panCardNumber = validatePanCardNumberOptional(profileData.panCardNumber);
        errors.currentLocation = validateRequired(profileData.currentLocation);
        errors.keywords = (profileData.keywords && profileData.keywords.split(',').filter(Boolean).length > 0) ? '' : 'At least one keyword is required.';
        errors.availableDays = availableDays.length > 0 ? '' : 'Select at least one working day.';

        const filteredErrors = Object.fromEntries(
            Object.entries(errors).filter(([_, val]) => val !== ''),
        );

        setFormErrors(filteredErrors);
        return Object.keys(filteredErrors).length === 0;
    }, [profileData, availableDays]);


    // handleSpecializationsChange, handleRemoveSpecialization, handleNewSpecializationInputChange, addSpecialization
    // These functions are no longer needed for direct user interaction as the fields are now display-only.
    // They are kept here but will not be triggered from the UI.
    const handleSpecializationsChange = useCallback((event) => {
        const {
            target: { value },
        } = event;
        const newValue = Array.isArray(value) ? value : (typeof value === 'string' ? value.split(',') : []);
        setSelectedSpecializations(newValue.map((item) => item.trim()).filter(Boolean));
    }, []);

    const handleRemoveSpecialization = useCallback((valueToRemove) => {
        setSelectedSpecializations((prev) => (prev || []).filter((v) => v !== valueToRemove));
    }, []);

    const handleNewSpecializationInputChange = useCallback((serviceType, value) => {
        setNewSpecializationInput((prev) => ({
            ...prev,
            [serviceType]: value,
        }));
    }, []);

    const addSpecialization = useCallback((serviceType) => {
        const value = (newSpecializationInput[serviceType] || '').trim();
        if (!value) {
            setSnackbar({ open: true, message: 'Specialization cannot be empty.', severity: 'warning' });
            return;
        }
        const lowerCaseValue = value.toLowerCase();
        const service = allServiceTypesData.find((s) => s.type_name === serviceType);
        const existsInMaster = service && (service.specializations || []).some((spec) => spec.toLowerCase() === lowerCaseValue);

        if (existsInMaster) {
            if (!(selectedSpecializations || []).some((spec) => spec.toLowerCase() === lowerCaseValue)) {
                setSelectedSpecializations((prev) => [...(prev || []), value]);
                setSnackbar({ open: true, message: `"${value}" selected.`, severity: 'success' });
            } else {
                setSnackbar({ open: true, message: `"${value}" is already added and selected for ${serviceType}.`, severity: 'info' });
            }
            setNewSpecializationInput((prev) => ({ ...prev, [serviceType]: '' }));
            return;
        }

        setAllServiceTypesData((prevServiceTypes) => prevServiceTypes.map((s) => (s.type_name === serviceType
            ? { ...s, specializations: [...(s.specializations || []), value] }
            : s)));

        setSelectedSpecializations((prevSelectedSpecs) => {
            const currentSpecs = prevSelectedSpecs || [];
            if (currentSpecs.some((spec) => spec.toLowerCase() === lowerCaseValue)) {
                return prevSelectedSpecs;
            }
            return [...currentSpecs, value];
        });

        setNewSpecializationInput((prev) => ({
            ...prev,
            [serviceType]: '',
        }));
        setSnackbar({ open: true, message: `Added "${value}" to ${serviceType}.`, severity: 'success' });
    }, [newSpecializationInput, allServiceTypesData, selectedSpecializations, setSnackbar]);


    const handleGetLocation = useCallback(() => {
        setIsFetchingLocation(true);

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const latitude = position.coords.latitude;
                    const longitude = position.coords.longitude;

                    try {
                        const response = await fetch(
                            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
                        );
                        if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        const data = await response.json();
                        const placeName = data.display_name || 'Location not found';
                        setIsFetchingLocation(false);

                        setLocation({ latitude, longitude, place: placeName });
                        setProfileData((prevData) => ({
                            ...prevData,
                            currentLocation: placeName,
                            latitude: latitude.toString(),
                            longitude: longitude.toString(),
                        }));
                        setSnackbar({ open: true, message: 'Location fetched successfully!', severity: 'success' });
                    } catch (error) {
                        console.error('Error fetching place name:', error);
                        setIsFetchingLocation(false);
                        setLocation({ latitude: '', longitude: '', place: 'Unknown location' });
                        setProfileData((prevData) => ({
                            ...prevData,
                            currentLocation: 'Error fetching location',
                            latitude: '',
                            longitude: '',
                        }));
                        setSnackbar({ open: true, message: 'Failed to resolve location name.', severity: 'error' });
                    }
                },
                (error) => {
                    console.error('Error fetching location:', error);
                    setIsFetchingLocation(false);
                    setSnackbar({ open: true, message: 'Failed to get location. Please enable location services and try again.', severity: 'error' });
                    setLocation({ latitude: '', longitude: '', place: '' });
                    setProfileData((prevData) => ({
                        ...prevData,
                        currentLocation: '',
                        latitude: '',
                        longitude: '',
                    }));
                },
                {
                    enableHighAccuracy: true,
                    timeout: 15000,
                    maximumAge: 0,
                },
            );
        } else {
            console.error('Geolocation is not supported by this browser.');
            setIsFetchingLocation(false);
            setSnackbar({ open: true, message: 'Your browser does not support Geolocation.', severity: 'error' });
        }
    }, [setProfileData, setSnackbar, setIsFetchingLocation, setLocation]);


    const handleUploadImage = useCallback(async (field) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = field === 'workVideo' ? 'video/*' : 'image/*';
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;

            const maxSize = field === 'workVideo' ? 10 * 1024 * 1024 : 2 * 1024 * 1024; // 10MB for video, 2MB for image
            if (file.size > maxSize) {
                setSnackbar({ open: true, message: `File size should not exceed ${maxSize / (1024 * 1024)}MB.`, severity: 'error' });
                return;
            }

            const fieldTypeMap = {
                profilePhoto: 'profile_photo',
                workPlaceGpsPhoto: 'work_place_gps',
                workVideo: 'work_video',
            };

            const apiFieldType = fieldTypeMap[field] || field;

            const formData = new FormData();
            formData.append('user_id', profileData.mobileNumber); // Assuming mobileNumber is the user_id
            formData.append('type', apiFieldType);
            formData.append('file', file);

            try {
                const response = await api.put('/services_register/upload_image', formData);
                if (response.data.status === 1) {
                    setSnackbar({ open: true, message: 'Upload successful!', severity: 'success' });
                    fetchVendorProfile(email); // Re-fetch all profile data to update UI
                } else {
                    setSnackbar({ open: true, message: response.data.message || 'Upload failed!', severity: 'error' });
                }
            } catch (error) {
                console.error('Upload error:', error);
                setSnackbar({ open: true, message: 'Upload failed due to server error.', severity: 'error' });
            }
        };
    }, [profileData.mobileNumber, email, fetchVendorProfile, setSnackbar]);


    const handleAddMultiImage = useCallback(async (field) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;

            const maxSize = 2 * 1024 * 1024; // 2MB for images
            if (file.size > maxSize) {
                setSnackbar({ open: true, message: 'Image size should not exceed 2MB.', severity: 'error' });
                return;
            }

            const formData = new FormData();
            formData.append('email', profileData.email); // Assuming email is needed for multi-image API
            formData.append('field_name', field);
            formData.append('file', file);

            try {
                const response = await api.post('/services_register/add_vendor_image', formData);
                if (response.data.status === 1) {
                    setSnackbar({ open: true, message: 'Image added successfully', severity: 'success' });
                    fetchVendorProfile(email); // Re-fetch all profile data to update UI
                } else {
                    setSnackbar({ open: true, message: response.data.message || 'Failed to add image.', severity: 'error' });
                }
            } catch (error) {
                console.error('Add image error:', error);
                setSnackbar({ open: true, message: 'An error occurred while adding the image.', severity: 'error' });
            }
        };
    }, [profileData.email, email, fetchVendorProfile, setSnackbar]);


    const handleRemoveImage = useCallback(async (field, imgPath) => {
        const formData = new FormData();
        formData.append('email', profileData.email);
        formData.append('field_name', field);
        formData.append('file_path', imgPath);

        try {
            const response = await api.post('/services_register/remove_vendor_images', formData);
            if (response.data.status === 1) {
                setSnackbar({ open: true, message: 'Image removed successfully', severity: 'success' });
                fetchVendorProfile(email); // Re-fetch all profile data to update UI
            } else {
                setSnackbar({ open: true, message: response.data.message || 'Failed to remove image!', severity: 'error' });
            }
        } catch (error) {
            console.error('Remove error:', error);
            setSnackbar({ open: true, message: 'Error occurred while removing image.', severity: 'error' });
        }
    }, [profileData.email, email, fetchVendorProfile, setSnackbar]);

    const handleEditImage = useCallback((field) => {
        handleUploadImage(field);
    }, [handleUploadImage]);

    const handleAddExperienceCertificate = useCallback(() => {
        handleAddMultiImage('experience_certificates');
    }, [handleAddMultiImage]);

    const handleAddIdProof = useCallback(() => {
        handleAddMultiImage('idproof_photo');
    }, [handleAddMultiImage]);

    const handleSave = useCallback(() => {
        if (!validateProfileForm()) {
            Swal.fire({
                title: 'Validation Error',
                text: 'Please correct the highlighted errors.',
                icon: 'warning',
            });
            return;
        }

        const formData = new FormData();

        Object.keys(profileData).forEach((key) => {
            if (
                ![
                    'profilePhoto', 'idProof', 'workPhoto', 'workVideo',
                    'experianceCertificate', 'workPlaceGpsPhoto',
                    'serviceType', 'availableDays', 'subService',
                ].includes(key)
            ) {
                formData.append(key, profileData[key] !== null && profileData[key] !== undefined ? profileData[key] : '');
            }
        });

        // These values are taken directly from the component's state (selectedServiceTypes and selectedSpecializations),
        // regardless of whether the input fields are visually disabled.
        // The expectation is that the backend will correctly receive and preserve these values.
        const cleanedServiceTypes = selectedServiceTypes
            .map((s) => s.trim())
            .filter(Boolean);
        formData.append('services_type', cleanedServiceTypes.join(','));

        const subServicesObject = {};
        cleanedServiceTypes.forEach((serviceType) => {
            const matchedService = allServiceTypesData.find((s) => s.type_name === serviceType);
            const specsForThisType = (matchedService?.specializations || []).filter((spec) =>
                selectedSpecializations.includes(spec),
            );
            subServicesObject[serviceType] = specsForThisType.join(',');
        });
        formData.append('subservices', JSON.stringify(subServicesObject));

        const cleanedDays = availableDays
            .map((d) => d.trim().toLowerCase())
            .filter(Boolean);
        formData.append('availabiledays', cleanedDays.join(','));

        formData.append('latitude', profileData.latitude || '');
        formData.append('longitude', profileData.longitude || '');


        api.post('/services_register/update_service_profile', formData)
            .then((response) => {
                if (response.data.status === 1) {
                    Swal.fire({
                        title: 'User Profile Updated Successfully!',
                        icon: 'success',
                        timer: 1500,
                        showConfirmButton: false,
                    });
                    fetchVendorProfile(email); // Trigger re-fetch of profile data
                    fetchCompletion(); // Trigger re-fetch of completion percentage
                } else {
                    Swal.fire({
                        title: 'Update Failed',
                        text: response.data.msg || 'Unknown error occurred.',
                        icon: 'error',
                    });
                }
            })
            .catch((error) => {
                console.error('Error updating profile:', error);
                Swal.fire({
                    title: 'Server Error',
                    text: 'An error occurred while saving. Please try again later.',
                    icon: 'error',
                });
            });
    }, [email, profileData, selectedServiceTypes, allServiceTypesData, selectedSpecializations, availableDays, validateProfileForm, fetchVendorProfile, fetchCompletion]);


    // 3. Core useEffect Hooks

    // Initial data fetch effect
    useEffect(() => {
        if (email) {
            const initialFetch = async () => {
                await fetchAllServiceTypesWithSpecializations();
                fetchVendorProfile(email);
                fetchCompletion();
            };
            initialFetch();
        }
    }, [email, fetchVendorProfile, fetchCompletion, fetchAllServiceTypesWithSpecializations]);


    // Effect to sync selected service types and specializations after profileData and allServiceTypesData are loaded
    useEffect(() => {
        if (allServiceTypesData.length === 0) return;

        const typesFromProfile = Array.isArray(profileData.serviceType)
            ? profileData.serviceType
            : (typeof profileData.serviceType === 'string' && profileData.serviceType.trim())
                ? profileData.serviceType.split(',').map((type) => type.trim())
                : [];
        if (JSON.stringify(selectedServiceTypes) !== JSON.stringify(typesFromProfile)) {
            setSelectedServiceTypes(typesFromProfile);
        }

        if (profileData.subService) {
            let parsedSpecs = [];
            try {
                const parsed = typeof profileData.subService === 'string'
                    ? JSON.parse(profileData.subService)
                    : profileData.subService;

                if (parsed && typeof parsed === 'object') {
                    Object.values(parsed).forEach((arr) => {
                        if (Array.isArray(arr)) {
                            arr.forEach((item) => {
                                if (typeof item === 'string' && item.trim()) {
                                    parsedSpecs.push(...item.split(',').map((s) => s.trim()).filter(Boolean));
                                }
                            });
                        }
                    });
                    parsedSpecs = [...new Set(parsedSpecs)];
                } else if (typeof profileData.subService === 'string' && profileData.subService.trim()) {
                    parsedSpecs = profileData.subService.split(',').map((s) => s.trim()).filter(Boolean);
                }
            } catch (e) {
                console.warn('Could not parse subService as JSON, attempting as comma-separated string.', e);
                if (typeof profileData.subService === 'string' && profileData.subService.trim()) {
                    parsedSpecs = profileData.subService.split(',').map((s) => s.trim()).filter(Boolean);
                }
            }
            if (JSON.stringify(selectedSpecializations) !== JSON.stringify(parsedSpecs)) {
                setSelectedSpecializations(parsedSpecs);
            }
        } else {
            if (selectedSpecializations.length > 0) {
                setSelectedSpecializations([]);
            }
        }
    }, [profileData.serviceType, profileData.subService, allServiceTypesData, selectedServiceTypes, selectedSpecializations]);


    // Effect to remove specializations related to deselected service types
    // This useEffect is still relevant to keep the `selectedSpecializations` state clean
    // based on `selectedServiceTypes`, even if the inputs are display-only.
    useEffect(() => {
        const allowedSpecializations = allServiceTypesData
            .filter((type) => selectedServiceTypes.includes(type.type_name))
            .flatMap((type) => (type.specializations || []).map((spec) => spec.trim()));

        setSelectedSpecializations((prevSelectedSpecs) => {
            const currentSpecs = prevSelectedSpecs || [];
            const updatedSpecs = currentSpecs.filter((spec) =>
                allowedSpecializations.includes(spec),
            );

            if (JSON.stringify(currentSpecs) !== JSON.stringify(updatedSpecs)) {
                return updatedSpecs;
            }
            return prevSelectedSpecs;
        });
    }, [selectedServiceTypes, allServiceTypesData, setSelectedSpecializations]);


    // 4. Component Styles (if not defined globally)
    const StyledTabs = styled(Tabs)({
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        '& .MuiTabs-indicator': { backgroundColor: '#0288d1', height: 4, borderRadius: 2 },
        '& .MuiTab-root': {
            fontWeight: 'bold',
            color: '#666',
            textTransform: 'capitalize',
            fontSize: 16,
            transition: '0.3s',
            '&:hover': { color: '#0288d1' },
        },
        '& .Mui-selected': { color: '#0288d1' },
    });

    const StyledCard = styled(Card)({
        width: '100%',
        padding: 16,
        borderRadius: 12,
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        transition: '0.3s',
        '&:hover': { transform: 'scale(1.03)' },
    });

    // 5. Render (JSX)
    return (
        <DashboardLayout>
 <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                <LogoutButton
                    onLogout={() => {
                        navigate('/login');
                    }}
                />
            </MDBox>

            <DashboardNavbar />
           
            {completion < 100 && (
                    <MDBox
                        sx={{
                              backgroundColor: '#e6ffed', // Light green for success
                            color: '#1a5d2c', // Dark green text
                            mt:3,
                            mb:-3,
                            py: 1,
                            px: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            gap: 2,
                            minHeight: '60px',
                            transform: 'translateY(0)',
                            transition: 'transform 0.3s ease-in-out',

                        }}
                    >
                        <Box sx={{ width: 40, height: 40 }}>
                            <Lottie animationData={profileIncompleteAnimation} loop autoplay />
                        </Box>
                        <MDTypography variant="body1" fontWeight="medium" color="text">
                            Your profile is **{completion.toFixed(0)}%** complete. Please fill out all required fields!
                        </MDTypography>
                    </MDBox>
                )}

                {/* NEW: On-screen banner for 100% profile completion (appears if completion === 100%) */}
                {show100PercentCompleteBanner && (
                    <MDBox
                        sx={{

                            backgroundColor: '#e6ffed', // Light green for success
                            color: '#1a5d2c', // Dark green text
                            py: 1,
                            px: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            gap: 2,
                            minHeight: '60px',
                            transform: 'translateY(0)',
                            transition: 'transform 0.3s ease-in-out',
                        }}
                    >
                        <CheckCircleOutlineIcon sx={{ fontSize: 40, color: '#28a745' }} />
                        <MDTypography variant="h6" fontWeight="bold" color="text">
                            Your profile is 100% complete! All features are now unlocked.
                        </MDTypography>
                    </MDBox>
                )}

             

            <MDBox pt={completion < 100 || show100PercentCompleteBanner ? '80px' : '30px'} m={3}>
                <Grid container spacing={3} direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Grid item>
                        <Typography variant="h5" fontWeight="bold">
                            User Profile
                        </Typography>
                    </Grid>

                    <Grid item>
                        <Box display="flex" justifyContent="center">
                            <VendorProfileWithCompletion
                                size={80}
                                textSize="30px"
                                completion={completion}
                                progressColor="#FE9900"
                                textColor="#0F08B4"
                                trailColor="#CCCCCC"
                            />
                        </Box>
                    </Grid>
                </Grid>
               

                <Grid item xs={12}>
                    <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: '8px', mb: 2 }}>
                        <Typography
                            variant="h6"
                            fontWeight="bold"
                            color="#1976d2"
                            gutterBottom
                            sx={{
                                mb: 4, mt: 2,
                            }}
                        >
                            Service Informations
                        </Typography>

                        <Grid container spacing={2}>

                            <Grid item xs={12} md={6} mt={1}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    {/* Display Service Types as static text */}
                                    <TextField
                                        label="Service Types"
                                        value={selectedServiceTypes.join(', ') || 'N/A'}
                                        InputProps={{ readOnly: true }}
                                        fullWidth
                                        variant="outlined"
                                        error={Boolean(formErrors.serviceType)}
                                        helperText={formErrors.serviceType}
                                    />
                                    {/* Removed FormControlLabel for "Add Custom Service Type" and the associated TextField
                                        as these are no longer interactive elements for service type selection. */}
                                </Box>
                            </Grid>


                            <Grid item xs={12} md={6} mt={1}>
                                <FormControl fullWidth error={Boolean(formErrors.subService)}>
                                    <InputLabel id="specializations-label">Specializations</InputLabel>
                                    <Select
                                        labelId="specializations-label"
                                        multiple
                                        displayEmpty
                                        label="Specializations"
                                        value={Array.isArray(selectedSpecializations) ? selectedSpecializations : []}
                                        onChange={handleSpecializationsChange} /* This onChange handler will not be called when disabled */
                                        renderValue={(selected) => (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 0.5,
                                                    maxHeight: 80,
                                                    overflowY: 'auto',
                                                    p: 1,
                                                }}
                                            >
                                                {selected.map((value, index) => (
                                                    <Chip key={index} label={value} onDelete={() => handleRemoveSpecialization(value)} />
                                                ))}
                                            </Box>
                                        )}
                                        sx={{
                                            height: 'auto',
                                            minHeight: 42,
                                            maxHeight: 300,
                                            overflowY: 'auto',
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 400,
                                                    width: 350,
                                                },
                                            },
                                        }}
                                        disabled={true}
                                    >
                                        {allServiceTypesData.length > 0 ? (
                                            allServiceTypesData
                                                .filter((service) => selectedServiceTypes.includes(service.type_name))
                                                .flatMap((service) => [
                                                    <ListSubheader key={service.type_name}>{service.type_name}</ListSubheader>,

                                                    ...(service.specializations || []).map((spec, index) => {
                                                        const specValue = spec.trim();
                                                        return (
                                                            <MenuItem key={`${service.type_name}-${specValue}-${index}`} value={specValue}>
                                                                <Checkbox checked={selectedSpecializations.includes(specValue)} />
                                                                {specValue}
                                                            </MenuItem>
                                                        );
                                                    }),

                                                    <Box
                                                        key={`add-spec-${service.type_name}`}
                                                        sx={{ display: 'flex', alignItems: 'center', p: 1, mt: 1 }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <TextField
                                                            size="small"
                                                            variant="outlined"
                                                            placeholder={`Add to ${service.type_name}`}
                                                            value={newSpecializationInput[service.type_name] || ''}
                                                            onChange={(e) => handleNewSpecializationInputChange(service.type_name, e.target.value)}
                                                            onKeyDown={(e) => { /* This onKeyDown handler will not be called when disabled */
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    addSpecialization(service.type_name);
                                                                }
                                                            }}
                                                            sx={{ mr: 1, flexGrow: 1 }}
                                                            disabled={true}
                                                        />
                                                        <MDButton
                                                            variant="contained"
                                                            size="small"
                                                            color="info"
                                                            onClick={() => addSpecialization(service.type_name)} /* This onClick handler will not be called when disabled */
                                                            disabled={true}
                                                        >
                                                            Add
                                                        </MDButton>
                                                    </Box>,
                                                ])
                                        ) : (
                                            <MenuItem disabled>No specializations available for selected service types</MenuItem>
                                        )}
                                    </Select>
                                    {formErrors.subService && (
                                        <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                                            {formErrors.subService}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Name"
                                    name="ownerName"
                                    value={profileData.ownerName}
                                    onChange={handleInputChange}
                                    error={Boolean(formErrors.ownerName)}
                                    helperText={formErrors.ownerName}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    value={profileData.email}
                                    onChange={handleInputChange}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Mobile Number"
                                    name="mobileNumber"
                                    value={profileData.mobileNumber}
                                    onChange={handleInputChange}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Alternative Number"
                                    name="anotherMobileNumber"
                                    value={profileData.anotherMobileNumber}
                                    onChange={handleInputChange}
                                    error={Boolean(formErrors.anotherMobileNumber)}
                                    helperText={formErrors.anotherMobileNumber}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Describe about Your Service "
                                    name="details"
                                    value={profileData.details}
                                    onChange={handleInputChange}
                                    error={Boolean(formErrors.details)}
                                    helperText={formErrors.details}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Work Location"
                                    name="workLocation"
                                    value={profileData.workLocation}
                                    onChange={handleInputChange}
                                    error={Boolean(formErrors.workLocation)}
                                    helperText={formErrors.workLocation}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Home Address"
                                    name="homeAddress"
                                    value={profileData.homeAddress}
                                    onChange={handleInputChange}
                                    error={Boolean(formErrors.homeAddress)}
                                    helperText={formErrors.homeAddress}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Experiance"
                                    name="experiance"
                                    value={profileData.experiance}
                                    onChange={handleInputChange}
                                    error={Boolean(formErrors.experiance)}
                                    helperText={formErrors.experiance}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Account Number"
                                    name="accountNo"
                                    value={profileData.accountNo}
                                    onChange={handleInputChange}
                                    error={Boolean(formErrors.accountNo)}
                                    helperText={formErrors.accountNo}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="IFSC Code"
                                    name="ifscCode"
                                    value={profileData.ifscCode}
                                    onChange={handleInputChange}
                                    error={Boolean(formErrors.ifscCode)}
                                    helperText={formErrors.ifscCode}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Passbook Name"
                                    name="passbookName"
                                    value={profileData.passbookName}
                                    onChange={handleInputChange}
                                    error={Boolean(formErrors.passbookName)}
                                    helperText={formErrors.passbookName}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Branch Name"
                                    name="branchName"
                                    value={profileData.branchName}
                                    onChange={handleInputChange}
                                    error={Boolean(formErrors.branchName)}
                                    helperText={formErrors.branchName}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Bank Name"
                                    name="bankName"
                                    value={profileData.bankName}
                                    onChange={handleInputChange}
                                    error={Boolean(formErrors.bankName)}
                                    helperText={formErrors.bankName}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="PAN Card Number"
                                    name="panCardNumber"
                                    value={profileData.panCardNumber}
                                    onChange={handleInputChange}
                                    error={Boolean(formErrors.panCardNumber)}
                                    helperText={formErrors.panCardNumber}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Current Location"
                                    name="currentLocation"
                                    value={profileData.currentLocation}
                                    error={Boolean(formErrors.currentLocation)}
                                    helperText={formErrors.currentLocation}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={handleGetLocation} disabled={isFetchingLocation}>
                                                    {isFetchingLocation ? <CircularProgress size={24} /> : <MyLocationIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6} mt={-2}>
                                <KeywordsInput
                                    label="Service Related Keywords"
                                    keywords={profileData.keywords ? profileData.keywords.split(',') : []}
                                    onChange={(newKeywords) =>
                                        handleInputChange({ target: { name: 'keywords', value: newKeywords.join(',') } })}
                                    error={Boolean(formErrors.keywords)}
                                    helperText={formErrors.keywords}
                                />
                            </Grid>

                        </Grid>
                    </Box>
                </Grid>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: '8px', mb: 2 }}>
                            <Typography variant="h6" fontWeight="bold" color="#1976d2" gutterBottom sx={{ mb: 4, mt: 2 }}>
                                Working Days
                            </Typography>
                            <Grid container spacing={1}>
                                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                                    <Grid item xs={6} sm={3} key={day}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={availableDays.includes(day)}
                                                    onChange={() => handleCheckboxChange(day)}
                                                    color="primary"
                                                />
                                            }
                                            label={day}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                            {formErrors.availableDays && (
                                <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                                    {formErrors.availableDays}
                                </Typography>
                            )}
                        </Box>
                    </Grid>


                    <Grid item xs={12}>
                        <MDBox display="flex" justifyContent="center" mt={2} mb={2}>
                            <MDButton variant="gradient" color="info" onClick={handleSave}>
                                Save Profile
                            </MDButton>
                        </MDBox>
                    </Grid>


                    <Grid container justifyContent="center" sx={{ width: '100%' }}>
                        <Grid item xs={12}>
                            <Box sx={{ width: '100%', p: 2 }}>
                                <StyledTabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} centered>
                                    <Tab label="Single Images" />
                                    <Tab label="Experience Certificates" />
                                    <Tab label="Gov ID Proof (Front & Back)" />
                                    <Tab label="Work Photos" />
                                </StyledTabs>
                                <Box sx={{ mt: 4, border: '1px solid #ccc', borderRadius: 2, p: 3, backgroundColor: '#f9f9f9' }}>
                                    {activeTab === 0 && (
                                        <Grid container spacing={3} justifyContent="center">
                                            {[
                                                { field: 'profilePhoto', label: 'Profile Photo', isVideo: false },
                                                { field: 'workPlaceGpsPhoto', label: 'Work Place GPS Photo', isVideo: false },
                                                { field: 'workVideo', label: 'Work Video', isVideo: true },
                                            ].map(({ field, label, isVideo }) => {
                                                const media = profileData[field];
                                                const hasMedia = media?.trim();
                                                return (
                                                    <Grid item xs={12} sm={6} md={3} key={field}>
                                                        <StyledCard>
                                                            {hasMedia ? (
                                                                <>
                                                                    {isVideo ? (
                                                                        <video controls width="100%" style={{ height: 180, borderRadius: 8, objectFit: 'contain' }}>
                                                                            <source src={getImageUrl(media)} type="video/mp4" />
                                                                            Your browser does not support the video tag.
                                                                        </video>
                                                                    ) : (
                                                                        <CardMedia
                                                                            component="img"
                                                                            image={getImageUrl(media)}
                                                                            alt={label}
                                                                            sx={{ height: 180, objectFit: 'contain', borderRadius: 2 }}
                                                                        />
                                                                    )}
                                                                    <Typography variant="body2" sx={{ mt: 1 }}>{label}</Typography>
                                                                    <IconButton color="primary" onClick={() => handleEditImage(field)}>
                                                                        <EditOutlinedIcon />
                                                                    </IconButton>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <ImageNotSupportedIcon sx={{ fontSize: 60, color: '#ccc' }} />
                                                                    <Typography variant="body2" sx={{ mt: 1 }}>{label}</Typography>
                                                                    <IconButton color="primary" onClick={() => handleUploadImage(field)}>
                                                                        <AddCircleOutlineIcon />
                                                                    </IconButton>
                                                                </>
                                                            )}
                                                        </StyledCard>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    )}

                                    {activeTab === 1 && (
                                        <Grid container spacing={3} justifyContent="center">
                                            {(profileData.experianceCertificate || '')
                                                .split(',')
                                                .filter(Boolean)
                                                .map((img, idx) => (
                                                    <Grid item xs={12} sm={6} md={3} key={`exp-${idx}`}>
                                                        <StyledCard>
                                                            <CardMedia
                                                                component="img"
                                                                image={getImageUrl(img)}
                                                                alt={`Certificate ${idx + 1}`}
                                                                sx={{ height: 180, objectFit: 'contain', borderRadius: 2 }}
                                                            />
                                                            <Typography variant="body2">Certificate {idx + 1}</Typography>
                                                            <IconButton color="error" onClick={() => handleRemoveImage('experience_certificates', img)}>
                                                                <DeleteOutlineIcon />
                                                            </IconButton>
                                                        </StyledCard>
                                                    </Grid>
                                                ))}
                                            <Grid item xs={12} sx={{ textAlign: 'center', mt: 2 }}>
                                                <IconButton color="primary" onClick={handleAddExperienceCertificate}>
                                                    <AddCircleOutlineIcon fontSize="large" />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    )}

                                    {activeTab === 2 && (
                                        <Grid container spacing={3} justifyContent="center">
                                            {(profileData.idProof || '')
                                                .split(',')
                                                .filter(Boolean)
                                                .map((img, idx) => (
                                                    <Grid item xs={12} sm={6} md={3} key={`id-${idx}`}>
                                                        <StyledCard>
                                                            <CardMedia
                                                                component="img"
                                                                image={getImageUrl(img)}
                                                                alt={`ID Proof ${idx + 1}`}
                                                                sx={{ height: 180, objectFit: 'contain', borderRadius: 2 }}
                                                            />
                                                            <Typography variant="body2">Gov ID Proof {idx + 1}</Typography>
                                                            <IconButton color="error" onClick={() => handleRemoveImage('idproof_photo', img)}>
                                                                <DeleteOutlineIcon />
                                                            </IconButton>
                                                        </StyledCard>
                                                    </Grid>
                                                ))}
                                            <Grid item xs={12} sx={{ textAlign: 'center', mt: 2 }}>
                                                <IconButton color="primary" onClick={handleAddIdProof}>
                                                    <AddCircleOutlineIcon fontSize="large" />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    )}
                                    {activeTab === 3 && (
                                        <Grid container spacing={3} justifyContent="center">
                                            {(profileData.workPhoto || '')
                                                .split(',')
                                                .filter(Boolean)
                                                .map((img, idx) => (
                                                    <Grid item xs={12} sm={6} md={3} key={`work-${idx}`}>
                                                        <StyledCard>
                                                            <CardMedia
                                                                component="img"
                                                                image={getImageUrl(img)}
                                                                alt={`Work Photo ${idx + 1}`}
                                                                sx={{ height: 180, objectFit: 'contain', borderRadius: 2 }}
                                                            />
                                                            <Typography variant="body2">Work Photo {idx + 1}</Typography>
                                                            <IconButton color="error" onClick={() => handleRemoveImage('work_photo', img)}>
                                                                <DeleteOutlineIcon />
                                                            </IconButton>
                                                        </StyledCard>
                                                    </Grid>
                                                ))}
                                            <Grid item xs={12} sx={{ textAlign: 'center', mt: 2 }}>
                                                <IconButton color="primary" onClick={() => handleAddMultiImage('work_photo')}>
                                                    <AddCircleOutlineIcon fontSize="large" />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                </Grid>
            </MDBox>
            {/* General Snackbar for various messages (not the 100% completion notification) */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <MuiAlert elevation={6} variant="filled" severity={snackbar.severity}>
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default UserProfile;